// @flow

import * as React from "react";
import {
  Button,
  ButtonsGroup,
  ConfirmationModalLauncher,
  Text,
  FlashMessageConsumer
} from "buba-components";
import { withRouter } from "react-router";
import { logError } from "buba-components/src/utils/loggers";
import { mapUnprocessableEntityError } from "buba-components/src/data/statusErrors";
import sharedStyles from "../shared.scss";
import {
  setStatusDeclineByBank,
  setStatusSubmitOffer,
  uploadAndCheckFile
} from "../../../../api/actions";
import BindingOfferModalContent from "./BindingOfferModalContent";
import SetStatusModalLauncher from "../SetStatusModalLauncher";
import paths from "../../../../data/paths";

type ActionsForInProgressProps = {
  id: number,
  onError: string => void,
  displayErrorForStatusCode: (?number) => void,
  requestFetch: any => void,
  history: {
    push: (path: string) => void
  }
};

type ActionsForInProgressState = {
  file: ?File,
  isLoading: boolean
};

class ActionsForInProgress extends React.Component<
  ActionsForInProgressProps,
  ActionsForInProgressState
> {
  state = {
    isLoading: false,
    file: null
  };

  setFile = (file: ?File) => this.setState({ file });

  setIsLoading = (isLoading: boolean) => this.setState({ isLoading });

  render() {
    const { id, onError, displayErrorForStatusCode, requestFetch, history } = this.props;
    const { file, isLoading } = this.state;

    return (
      <FlashMessageConsumer>
        {({ addMessage }) => (
          <>
            <header>Abgabe verbindliches Angebot</header>
            <Text additionalClasses={sharedStyles.actionText}>
              Um dem Kunden eine Finanzierung zu ermöglichen, bestätigen Sie hier die
              Angebotsstellung und laden Sie dieses für den Kunden hoch. Andernfalls klicken Sie
              bitte auf &quot;KEIN INTERESSE&quot;. Die Anfrage kann dann an andere Banken
              weitergeleitet werden, um den Kundenwunsch zu bedienen.
            </Text>
            <ButtonsGroup>
              <ConfirmationModalLauncher
                trigger={({ open }) => (
                  <Button
                    onClick={() => {
                      this.setFile(null);
                      open();
                    }}
                    size="small"
                  >
                    Genehmigen (Verbindliches Angebot)
                  </Button>
                )}
                content={() => (
                  <BindingOfferModalContent
                    setFile={this.setFile}
                    displayErrorForStatusCode={displayErrorForStatusCode}
                    fileName={file && file.name}
                    isLoading={isLoading}
                  />
                )}
                confirmButtonText="Genehmigung abschliessen"
                declineButtonText="Genehmigung abbrechen"
                isConfirmButtonLoading={isLoading}
                isConfirmButtonDisabled={!file || isLoading}
                isDeclineButtonDisabled={isLoading}
                confirmAction={async ({ close }) => {
                  try {
                    if (file) {
                      this.setIsLoading(true);
                      const { status } = await uploadAndCheckFile({ id, file }, { addMessage });

                      if (status === "rejected") {
                        displayErrorForStatusCode(null);
                        close();
                      }

                      await setStatusSubmitOffer(id);
                      this.setIsLoading(false);
                    }

                    requestFetch();
                    close();
                  } catch (err) {
                    const { response } = err;
                    const status = response && response.status;

                    if (status === 422) {
                      const errors = response && response.data && response.data.errors;

                      if (Array.isArray(errors)) {
                        errors.forEach(e => onError(mapUnprocessableEntityError(e.type)));
                      }
                    } else {
                      displayErrorForStatusCode(status);
                    }
                    logError(err);
                    this.setIsLoading(false);

                    close();
                  }
                }}
                declineAction={({ close }) => close()}
              />
              <SetStatusModalLauncher
                id={id}
                triggerButtonText="Kein Interesse"
                contentText="Sie Sind im Begriff, diese Anfrage abzulehnen. Sind Sie sicher?"
                displayErrorForStatusCode={displayErrorForStatusCode}
                requestFetch={() =>
                  new Promise(resolve => {
                    history.push(paths.customerInquiriesList);
                    resolve();
                  })
                }
                setStatusAction={setStatusDeclineByBank}
                statusName="declined by bank"
              />
            </ButtonsGroup>
          </>
        )}
      </FlashMessageConsumer>
    );
  }
}

export default withRouter(ActionsForInProgress);
